// MessageComposer.jsx
import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import PageTitle from "../../../widgets/PageTitle";
import WabaTextMsg from "../../../widgets/WabaTextMsg";
import { API_ENDPOINT } from "../../../utils/constants";
import { getHeader, toastError } from "../../../utils/util";
import { toast } from "react-toastify";
import { nanoid } from "nanoid";
import SaveButton from "./SaveButton";
import SectionLabel from "../../../widgets/SectionLabel";

/* --------------------------------------------------------------------------- */
/* ------------------------------ VALIDATION --------------------------------- */
/* --------------------------------------------------------------------------- */
const productSchema = yup.object({
  id: yup.string().trim().required("ID required"),
  title: yup.string().trim().required("Title required"),
});

const sectionSchema = yup.object({
  title: yup.string().trim().required("Section title is required"),
  rows: yup
    .array()
    .of(productSchema)
    .min(1, "Add at least one row in this section")
    .max(10, "Maximum 10 rows per section"),
});

const createSchema = (isEdit, preview) =>
  yup.object({
    headerText: yup.string().trim(),
    body: yup.string().trim().required("Body text is required"),
    footerText: yup.string().trim(),
    buttonText: yup
      .string()
      .trim()
      .required("Button text is required")
      .max(20, "Button text must be 20 characters or less"),
    triggerText:
      isEdit || preview
        ? yup.string().trim()
        : yup.string().trim().required("Trigger text is required"),
    sections: yup
      .array()
      .of(sectionSchema)
      .min(1, "Add at least one section")
      .max(10, "Maximum 10 sections"),
  });

/* --------------------------------------------------------------------------- */
/* ------------------------------ COMPONENT ---------------------------------- */
/* --------------------------------------------------------------------------- */
export default function MessageComposer({
  value,
  isEdit = false,
  editId,
  preview = false,
  onUpdate,
}) {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      headerText: value?.header?.text ?? "",
      body: value?.body ?? "",
      footerText: "",
      buttonText: value?.buttonText ?? "",
      triggerText: "",
      sections: value?.sections ?? [
        {
          title: "",
          rows: [{ id: nanoid(), title: "", description: "" }],
        },
      ],
    },
    resolver: yupResolver(createSchema(isEdit, preview)),
    mode: "onChange",
  });

  const {
    fields: sectionFields,
    append: appendSection,
    remove: removeSection,
  } = useFieldArray({
    control,
    name: "sections",
  });

  /* ---------------------------- SUBMIT HANDLER ---------------------------- */
  const onSubmit = async (data) => {
    const payloadObject = {
      type: "interactive_list",
      ...data,
    };
    if (isEdit) {
      payloadObject.id = editId;
      payloadObject.isEdit = true;
      delete payloadObject.triggerText;
    }
    if (preview) {
      onUpdate?.({ ...data, type: "list" });
      return;
    }
    try {
      const response = await axios.post(
        `${API_ENDPOINT}/message/save-message`,
        payloadObject,
        getHeader()
      );
      toast.success(response.data.message);
    } catch (err) {
      toastError(err);
    }
  };

  /* ----------------------------- RENDER UI -------------------------------- */
  return (
    <Paper elevation={0} sx={{ px: 4, py: 2 }}>
      {preview ? (
        <SectionLabel>Interactive List</SectionLabel>
      ) : (
        <PageTitle>Interactive List</PageTitle>
      )}
      <Box mt={2} component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <Controller
            name="headerText"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                label="Header text (optional)"
                fullWidth
                error={!!errors.headerText}
                helperText={errors.headerText?.message}
              />
            )}
          />

          <Controller
            name="body"
            control={control}
            render={({ field }) => (
              <WabaTextMsg
                {...field}
                label="Body text *"
                fullWidth
                multiline
                minRows={3}
                error={!!errors.body}
                helperText={errors.body?.message}
              />
            )}
          />

          <Controller
            name="footerText"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Footer text (optional)"
                fullWidth
                size="small"
                error={!!errors.footerText}
                helperText={errors.footerText?.message}
              />
            )}
          />

          <Controller
            name="buttonText"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Button text"
                fullWidth
                size="small"
                required
                error={!!errors.buttonText}
                helperText={errors.buttonText?.message || "Max 20 characters"}
              />
            )}
          />
          {!preview ? (
            <Controller
              name="triggerText"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Trigger text"
                  fullWidth
                  size="small"
                  required
                  error={!!errors.triggerText}
                  helperText={errors.triggerText?.message}
                />
              )}
            />
          ) : (
            <></>
          )}
        </Stack>
        <Divider sx={{ my: 4 }} />
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" flexGrow={1}>
            Sections
          </Typography>
          <IconButton
            color="primary"
            aria-label="add section"
            disabled={sectionFields.length >= 10}
            onClick={() =>
              appendSection({
                title: "",
                rows: [{ id: nanoid(), title: "", description: "" }],
              })
            }
          >
            <Add />
          </IconButton>
        </Stack>
        {errors.sections?.message && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {errors.sections.message}
          </Typography>
        )}

        {sectionFields.map((section, secIdx) => (
          <SectionCard
            key={section.id}
            secIdx={secIdx}
            control={control}
            removeSection={removeSection}
            sectionError={errors.sections?.[secIdx]}
          />
        ))}
        <SaveButton loading={isSubmitting} />
      </Box>
    </Paper>
  );
}
function SectionCard({ secIdx, control, removeSection, sectionError }) {
  const {
    fields: productFields,
    append: appendProduct,
    remove: removeProduct,
  } = useFieldArray({
    control,
    name: `sections.${secIdx}.rows`,
  });

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mt: 3, bgcolor: "grey.50", position: "relative" }}
    >
      {/* ---------- Section header (title + delete) ---------- */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={11} sm={11}>
          <Controller
            control={control}
            name={`sections.${secIdx}.title`}
            render={({ field }) => (
              <TextField
                {...field}
                label={`Section title *`}
                fullWidth
                size="small"
                error={!!sectionError?.title}
                helperText={sectionError?.title?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={1} sm={1}>
          <IconButton
            color="error"
            aria-label="delete section"
            size="small"
            disabled={secIdx === 0 && productFields.length === 1} // keep at least one section with one row
            onClick={() => removeSection(secIdx)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Grid>
      </Grid>

      {/* ---------- Products list header ---------- */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 3 }}>
        <Typography variant="subtitle1" flexGrow={1}>
          Rows
        </Typography>
        <IconButton
          color="primary"
          aria-label="add product"
          disabled={productFields.length >= 10}
          onClick={() =>
            appendProduct({ id: nanoid(), title: "", description: "" })
          }
        >
          <Add />
        </IconButton>
      </Stack>
      {sectionError?.rows?.message && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {sectionError.rows.message}
        </Typography>
      )}

      {/* ---------- Product rows ---------- */}
      {productFields.map((row, rowIdx) => (
        <Paper
          key={row.id}
          variant="outlined"
          sx={{ p: 2, my: 2, bgcolor: "grey.0" }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <Controller
                control={control}
                name={`sections.${secIdx}.rows.${rowIdx}.title`}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Title"
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: 24 }}
                    error={!!sectionError?.rows?.[rowIdx]?.title}
                    helperText={
                      sectionError?.rows?.[rowIdx]?.title?.message ||
                      "Only letters and spaces allowed (max 24 chars)"
                    }
                    onChange={(e) => {
                      const newValue = e.target.value
                        .replace(/[^a-zA-Z\s]/g, "")
                        .slice(0, 24);
                      field.onChange(newValue);
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name={`sections.${secIdx}.rows.${rowIdx}.description`}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description"
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: 72 }}
                    error={!!sectionError?.rows?.[rowIdx]?.description}
                    helperText={
                      sectionError?.rows?.[rowIdx]?.description?.message ||
                      "Only letters and spaces allowed (max 72 chars)"
                    }
                    onChange={(e) => {
                      const newValue = e.target.value
                        .replace(/[^a-zA-Z\s]/g, "")
                        .slice(0, 72);
                      field.onChange(newValue);
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={1}>
              <IconButton
                aria-label="delete row"
                color="error"
                size="small"
                disabled={productFields.length === 1}
                onClick={() => removeProduct(rowIdx)}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        </Paper>
      ))}
    </Paper>
  );
}
